import React from 'react';
import * as yup from 'yup';
import { Box, Typography } from '@mui/material';
import {
  SparkForm,
  SparkFormButton,
  SparkTextField,
} from 'sharedComponents/SparkForm';
import { SparkDialog } from 'sharedComponents/UI';
import SparkFormRating from 'sharedComponents/SparkForm/SparkFormRating';
import { SparkFormProps } from 'sharedComponents/SparkForm/SparkForm';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import { SparkImageLoader } from 'sharedComponents/UI';
import type { Rating } from 'types/common';

export type ReviewFormModalValues = {
  rating: number;
  comment: string;
};

type ReviewFormModalProps = {
  open: boolean;
  onClose: () => void;
  rating: Rating | undefined;
  allowedToReview: boolean | undefined;
  onSave: SparkFormProps<ReviewFormModalValues>['onSave'];
  logoURL: string | undefined;
  resourceName: string | undefined;
  setCurrentRating?: React.Dispatch<React.SetStateAction<number | null>>;
};

function ReviewFormModal({
  open,
  onClose,
  rating,
  allowedToReview = false,
  onSave,
  logoURL,
  resourceName,
  setCurrentRating,
}: ReviewFormModalProps) {
  const initialValues = {
    rating: rating?.rating ?? 0,
    comment: rating?.comment ?? '',
  };
  const validationSchema = yup.object().shape({
    rating: yup.number().required(),
    comment: yup.string().required('Comment are Required').max(500),
  });
  return (
    <SparkDialog open={open} onClose={onClose} title='' hideTitle={true}>
      <SparkForm<ReviewFormModalValues>
        initialValues={initialValues}
        onSave={onSave}
        validationSchema={validationSchema}
        sx={{ paddingX: 2 }}
      >
        <Box
          sx={{
            width: '100%',
            pt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: '42px',
              height: '42px',
              background:
                'linear-gradient(45deg, #F4821F, #EF5251, #DD529C, #6268AF)',
              border: '1px solid white',
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {logoURL ? (
              <SparkImageLoader
                width='42px'
                height='42px'
                style={{ borderRadius: '12px' }}
                src={logoURL}
                alt=''
              />
            ) : (
              <SparkdefaultIcon width={'42px'} height={'42px'} />
            )}
          </Box>

          <Typography fontSize={24} fontFamily='Mont-SemiBold'>
            What do you think of {resourceName} ?
          </Typography>
        </Box>
        <Box sx={{ width: '100%', marginTop: 4, pl: 0.5 }}>
          <Typography fontSize={16}>How would you rate it ?</Typography>
        </Box>

        <SparkFormRating
          name='rating'
          onChange={setCurrentRating}
          gridProps={{
            justifyContent: 'center',
            marginBottom: 6,
            marginTop: 5,
          }}
        />
        <SparkTextField
          name='comment'
          placeholder={`Tell us more about your experience with ${resourceName}?`}
          label={`What are your thoughts about ${resourceName}?`}
          multiline={true}
          rows={4}
          size={12}
          readonly={!allowedToReview}
          maxNumber={500}
          showCounter={true}
        />

        {allowedToReview && (
          <Box
            sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
          >
            <SparkFormButton fullWidth={false} shouldRequireFieldUpdates={true}>
              Submit
            </SparkFormButton>
          </Box>
        )}
      </SparkForm>
    </SparkDialog>
  );
}

export default ReviewFormModal;
